import React, { useState } from "react";
import userPlaceholder from "../../../assets/svg/profile/user.svg";
import type { ProfilePhoto } from "../types/settings.types";

interface ProfileAvatarProps {
  /**
   * Only the bytes to paint are asked for, not a whole `ProfilePhoto` — which
   * lets the photo card hand it the picture being uploaded, before there is an
   * id or a stored URL to go with it.
   */
  photo: Pick<ProfilePhoto, "dataUrl"> | null;
  /** What the initial is taken from, for a profile with no picture on it. */
  name: string;
  /** Tailwind size classes — the overview's small disc, the form's large one. */
  className?: string;
  /** The initial's type size, which does not scale with the disc on its own. */
  initialClassName?: string;
}

/**
 * The dietitian's picture, or the first letter of their name where there is
 * none — the same fallback a client's row gets, so a profile without a photo
 * is still something rather than an empty circle.
 *
 * Before the name is filled in there is no letter to fall back to either, and
 * a lone "?" reads as something having gone wrong rather than as a profile
 * waiting to be filled. The drawn figure stands in there instead.
 *
 * A picture that will not load falls back to the letter too. The photo is a
 * local read today and cannot fail, but it will be a URL from the API's origin
 * the moment there is an endpoint behind it, and that can.
 */
export const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  photo,
  name,
  className = "h-11 w-11",
  initialClassName = "text-[16px]",
}) => {
  const [failed, setFailed] = useState<string | null>(null);
  const broken = photo !== null && failed === photo.dataUrl;

  if (photo && !broken) {
    return (
      <img
        src={photo.dataUrl}
        alt=""
        onError={() => setFailed(photo.dataUrl)}
        className={`flex-shrink-0 rounded-full object-cover ${className}`}
      />
    );
  }

  const initial = name.trim().charAt(0).toUpperCase();

  // The file carries its own disc and background, so it only has to be sized.
  if (!initial) {
    return (
      <img
        src={userPlaceholder}
        alt=""
        aria-hidden="true"
        className={`flex-shrink-0 rounded-full ${className}`}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={`flex flex-shrink-0 items-center justify-center rounded-full bg-[#DEEBFB] ${className}`}
    >
      <span className={`font-medium text-[#2F6BAE] ${initialClassName}`}>
        {initial}
      </span>
    </span>
  );
};

export default ProfileAvatar;
